// src/components/dashboards/StudentDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { studentAPI } from '../../services/api';
import DashboardLayout from '../shared/DashboardLayout';
import StatCard from '../shared/StatCard';
import '../../styles/Dashboard.css';
import { classAPI, classAttendanceAPI, coeAPI, studentThesisAPI, hostelInventoryAPI } from '../../services/api';
import { ClassTimetableView, ClassCalendarView } from '../shared/ClassTimetableCalendar';
import { exportTimetableToPDF } from '../../utils/timetablePDF';
import StudentThesisProposalSection from './StudentThesisProposalSection';
import StudentEthicsSection from './StudentEthicsSection';
import MyBorrowedBooks from '../shared/MyBorrowedBooks';
import HostelUserPortalTab from '../hostel/HostelUserPortalTab';
import NewActivityForm from '../student/newActivity/NewActivityForm';
import Calendar from '../shared/Calendar';
import ActivitiesSection from '../student/activities/ActivitiesSection';
import StudentTransportViewTab from '../transport/StudentTransportViewTab';
import { notify, confirmDialog } from '../../utils/notify';


const MBBS_YEAR_LABELS = { 1: '1st Year', 2: '2nd Year', 3: '3rd Year', 4: '4th Year' };

function StudentClassScheduleTab() {
  const { user } = useAuth();
  const [classes,   setClasses]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [filter,    setFilter]    = useState('all');
  const [viewMode,  setViewMode]  = useState('list');

  const STATUS_COLORS = {
    PENDING:   { c: '#92400e', bg: '#fef3c7', border: '#fde68a' },
    ONGOING:   { c: '#1d4ed8', bg: '#dbeafe', border: '#bfdbfe' },
    COMPLETED: { c: '#166534', bg: '#dcfce7', border: '#86efac' },
    CANCELLED: { c: '#6b7280', bg: '#f3f4f6', border: '#e5e7eb' },
  };

  useEffect(() => {
    // Use the correct student endpoint — no 403
    classAPI.getStudentClasses({})
      .then(d => setClasses(Array.isArray(d) ? d : (d.results || [])))
      .catch(() => setClasses([]))
      .finally(() => setLoading(false));
  }, []);

  const displayed = filter === 'all' ? classes : classes.filter(c => c.status === filter);

  const counts = { PENDING: 0, ONGOING: 0, COMPLETED: 0 };
  classes.forEach(c => { if (counts[c.status] !== undefined) counts[c.status]++; });

  const fmtD = s => s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  if (loading) return <div style={{ textAlign: 'center', padding: 48, color: '#64748b' }}>Loading classes…</div>;

  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* PDF export */}
      {classes.length > 0 && (
        <div style={{ display:'flex', justifyContent:'flex-end' }}>
          <button
            onClick={() => exportTimetableToPDF({ classes, title: user?.profile?.course === 'PG' ? 'My Class Schedule' : 'My MBBS Class Schedule', institutionName: 'Medical College ERP', showFaculty: true, showStatus: true })}
            style={{ padding:'7px 14px', borderRadius:8, border:'1px solid #334155', background:'#fff', cursor:'pointer', fontSize:12, fontWeight:700, color:'#334155', fontFamily:'inherit' }}>
            🖨 Export PDF
          </button>
        </div>
      )}
      {/* Stats */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {[['Total', classes.length, '#334155', '#f1f5f9'], ['Pending', counts.PENDING, '#92400e', '#fef3c7'], ['Ongoing', counts.ONGOING, '#1d4ed8', '#dbeafe'], ['Completed', counts.COMPLETED, '#166534', '#dcfce7']].map(([l, v, c, bg]) => (
          <div key={l} style={{ flex: '1 1 100px', background: bg, borderRadius: 12, padding: '12px 16px' }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: c, lineHeight: 1 }}>{v}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: c, marginTop: 3 }}>{l}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      {/* View mode toggle */}
      <div style={{ display:'flex', gap:8, alignItems:'center', justifyContent:'flex-end' }}>
        <span style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'.04em' }}>View:</span>
        <div style={{ display:'flex', border:'1px solid #e2e8f0', borderRadius:8, overflow:'hidden' }}>
          {[['list','☰ List'],['timetable','🗓 Timetable'],['calendar','📆 Calendar']].map(([mode,label]) => (
            <button key={mode} onClick={() => setViewMode(mode)}
              style={{ padding:'6px 12px', border:'none', background:viewMode===mode?'#1d4ed8':'#fff', color:viewMode===mode?'#fff':'#374151', fontSize:11, fontWeight:600, cursor:'pointer', fontFamily:'inherit', borderRight:'1px solid #e2e8f0' }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {displayed.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 24px', color: '#94a3b8', background: '#f8fafc', borderRadius: 12, border: '2px dashed #e2e8f0' }}>
          No classes found for your curriculum.
        </div>
      ) : viewMode === 'timetable' ? (
        <ClassTimetableView
          classes={displayed}
          role="STUDENT"
        />
      ) : viewMode === 'calendar' ? (
        <ClassCalendarView
          classes={displayed}
          role="STUDENT"
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {displayed.map(cs => {
            const sc = STATUS_COLORS[cs.status] || STATUS_COLORS.PENDING;
            return (
              <div key={cs.id} style={{ background: '#fff', border: `1px solid ${cs.status === 'ONGOING' ? '#bfdbfe' : '#e5e7eb'}`, borderRadius: 14, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10, boxShadow: cs.status === 'ONGOING' ? '0 0 0 3px #dbeafe' : 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a', marginBottom: 3 }}>{cs.title}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      {cs.mbbs_year ? MBBS_YEAR_LABELS[cs.mbbs_year] + ' · ' : ''}{cs.department_name} · {cs.duration_display || 'Custom'}
                    </div>
                    {cs.time_slot_name && (
                      <div style={{ fontSize: 12, color: '#1d4ed8', marginTop: 3 }}>⏰ {cs.time_slot_name} · {cs.time_slot_start?.slice(0,5)}–{cs.time_slot_end?.slice(0,5)}</div>
                    )}
                  </div>
                  <span style={{ padding: '3px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: sc.bg, color: sc.c, border: `1px solid ${sc.border}`, whiteSpace: 'nowrap' }}>
                    {cs.status}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 8 }}>
                  {[['📅 Start', fmtD(cs.start_date)], ['🏁 End', fmtD(cs.end_date)], ['👤 Faculty', cs.assigned_to_name || 'Not assigned']].map(([l, v]) => (
                    <div key={l} style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 12px' }}>
                      <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 2 }}>{l}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{v}</div>
                    </div>
                  ))}
                </div>
                {cs.status_note && (
                  <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#0c4a6e' }}>
                    💬 {cs.status_note}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StudentAttendanceTracker() {
  const [year,     setYear]     = useState(new Date().getFullYear());
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    setLoading(true);
    classAttendanceAPI.getMyAttendance({ year })
      .then(d => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [year]);

  const ATT_COLOR = {
    PRESENT: { c: '#166534', bg: '#dcfce7' },
    ABSENT:  { c: '#991b1b', bg: '#fef2f2' },
    LATE:    { c: '#854d0e', bg: '#fef3c7' },
    EXCUSED: { c: '#4c1d95', bg: '#ede9fe' },
  };

  const stats    = data?.stats || {};
  const records  = data?.records || [];
  const pct      = stats.attendance_pct ?? 0;
  const pctColor = pct >= 75 ? '#16a34a' : pct >= 50 ? '#d97706' : '#dc2626';

  if (loading) return <div style={{ textAlign: 'center', padding: 48, color: '#64748b' }}>Loading attendance…</div>;

  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", display: 'flex', flexDirection: 'column', gap: 18 }}>

      {/* Year selector */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em' }}>Academic Year</label>
        <select value={year} onChange={e => setYear(Number(e.target.value))}
          style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff', fontFamily: 'inherit', cursor: 'pointer' }}>
          {[new Date().getFullYear(), new Date().getFullYear() - 1].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* Overall stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(130px,1fr))', gap: 12 }}>
        {[
          ['Total Sessions', stats.total ?? 0, '#334155', '#f1f5f9'],
          ['Present',        stats.present ?? 0, '#166534', '#dcfce7'],
          ['Absent',         stats.absent ?? 0, '#991b1b', '#fef2f2'],
          ['Excused',        stats.excused ?? 0, '#4c1d95', '#ede9fe'],
        ].map(([l, v, c, bg]) => (
          <div key={l} style={{ background: bg, borderRadius: 12, padding: '14px 16px' }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: c, lineHeight: 1 }}>{v}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: c, marginTop: 4 }}>{l}</div>
          </div>
        ))}
      </div>

      {/* Attendance rate meter */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '18px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Overall Attendance Rate ({year})</span>
          <span style={{ fontSize: 22, fontWeight: 800, color: pctColor }}>{pct}%</span>
        </div>
        <div style={{ height: 16, background: '#f1f5f9', borderRadius: 8, overflow: 'hidden', marginBottom: 8 }}>
          <div style={{ height: '100%', width: `${pct}%`, background: pctColor, borderRadius: 8, transition: 'width .6s' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94a3b8' }}>
          <span>{pct >= 75 ? '✓ Above 75% threshold' : '⚠ Below 75% minimum requirement'}</span>
          <span>75% minimum</span>
        </div>
      </div>

      {/* Records table */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #f1f5f9', fontSize: 13, fontWeight: 700, color: '#0f172a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>My Attendance Records</span>
          <span style={{ fontSize: 12, color: '#64748b' }}>{records.length} records in {year}</span>
        </div>
        {records.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 36, color: '#94a3b8', fontSize: 13 }}>No attendance records for {year}.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
              <thead style={{ background: '#f8fafc' }}>
                <tr>{['Class / Subject', 'Date', 'Status', 'Confirmed'].map(h => (
                  <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {records.slice().sort((a, b) => (b.attendance_date || '').localeCompare(a.attendance_date || '')).map((r, i) => {
                  const ac = ATT_COLOR[r.status] || ATT_COLOR.PRESENT;
                  return (
                    <tr key={r.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa', borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 600, fontSize: 13, color: '#0f172a' }}>{r.class_title || '—'}</td>
                      <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b', fontFamily: 'monospace' }}>{r.attendance_date || '—'}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: ac.bg, color: ac.c }}>{r.status_display || r.status}</span>
                      </td>
                      <td style={{ padding: '10px 14px', fontSize: 12 }}>
                        {r.is_confirmed
                          ? <span style={{ color: '#166534', fontWeight: 700 }}>✓ Yes</span>
                          : <span style={{ color: '#854d0e' }}>⏱ Pending</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: '14px 18px', fontSize: 13, color: '#1d4ed8' }}>
        <strong>📝 Mark your own attendance</strong> — For ongoing classes, you can self-mark your attendance. Faculty will confirm it.
      </div>
    </div>
  );
}

function StudentMyExaminationsTab({ exams }) {
  const [selectedDate, setSelectedDate] = useState('');
  const today = new Date().toISOString().split('T')[0];

  const upcoming  = exams.filter(e => e.exam_date >= today && !e.is_cancelled);
  const completed = exams.filter(e => e.exam_date < today  && !e.is_cancelled);

  const filteredExams = selectedDate
    ? exams.filter(e => e.exam_date === selectedDate)
    : exams;

  const activeExams = filteredExams.filter(e => !e.is_cancelled);
  const cancelled   = filteredExams.filter(e => e.is_cancelled);

  // Group active exams by date
  const groupedExams = activeExams.reduce((groups, exam) => {
    const d = exam.exam_date;
    if (!groups[d]) groups[d] = [];
    groups[d].push(exam);
    return groups;
  }, {});

  const sortedDates = Object.keys(groupedExams).sort((a, b) => new Date(a) - new Date(b));

  const TYPE_COLORS = {
    MAIN:          { bg: '#dbeafe', c: '#1d4ed8' },
    PRACTICAL:     { bg: '#dcfce7', c: '#166534' },
    INTERNAL:      { bg: '#fef3c7', c: '#92400e' },
    SUPPLEMENTARY: { bg: '#fce7f3', c: '#9d174d' },
    VIVA:          { bg: '#ede9fe', c: '#5b21b6' },
    CLINICAL:      { bg: '#ccfbf1', c: '#065f46' },
    UNIVERSITY:    { bg: '#fee2e2', c: '#991b1b' },
    OTHER:         { bg: '#f3f4f6', c: '#374151' },
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' });
  };
  const fmtTime = (t) => {
    if (!t) return '—';
    const [h, m] = t.split(':');
    const hr = parseInt(h);
    return `${hr % 12 || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
  };

  const ExamCard = ({ exam }) => {
    const tc = TYPE_COLORS[exam.exam_type] || TYPE_COLORS.OTHER;
    const isPast = exam.exam_date < today;
    return (
      <div style={{
        background: '#fff',
        border: `1px solid ${isPast ? '#e5e7eb' : 'rgb(49,154,185)'}`,
        borderLeft: `4px solid ${isPast ? '#d1d5db' : 'rgb(49,154,185)'}`,
        borderRadius: 12,
        padding: '16px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>
              {exam.paper_label || exam.subject}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              {exam.department_name} · {exam.timetable_title}
            </div>
          </div>
          <span style={{ padding: '3px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: tc.bg, color: tc.c, whiteSpace: 'nowrap' }}>
            {exam.exam_type_display} · {exam.sub_type_display}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
          {[
            ['⏰ Time',    `${fmtTime(exam.start_time)} – ${fmtTime(exam.end_time)}`],
            ['📍 Venue',   exam.venue || 'TBA'],
            ['📊 Marks',   `${exam.max_marks} (Pass: ${exam.pass_marks})`],
            ['📄 Paper',   exam.paper_label ? `Paper ${exam.paper_number}` : '—'],
            ['🎫 Roll No', exam.roll_number || exam.student_id_num || 'TBA'],
          ].map(([l, v]) => (
            <div key={l} style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 10px' }}>
              <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, marginBottom: 2 }}>{l}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>{v}</div>
            </div>
          ))}
        </div>

        {/* University Theory — Paper Evaluator marks + mean */}
        {exam.exam_type === 'UNIVERSITY' && exam.sub_type === 'THEORY' && (() => {
          const pe = exam.paper_evaluations || { evaluations: [], avg_marks: null, submitted_count: 0, total_evaluators: 0 };
          const submitted = pe.evaluations.filter(e => e.status === 'SUBMITTED');
          if (submitted.length > 0) {
            return (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#166534', textTransform: 'uppercase', marginBottom: 10 }}>Evaluation Results</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 10 }}>
                  {pe.evaluations.map((ev, idx) => (
                    <div key={idx} style={{ flex: '1 1 100px', background: '#fff', border: '1px solid #d1fae5', borderRadius: 8, padding: '8px 10px' }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#166534', textTransform: 'uppercase', marginBottom: 4 }}>Evaluator {idx + 1}</div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: '#14532d' }}>{ev.status === 'SUBMITTED' ? ev.total_marks : '—'}</div>
                      <div style={{ fontSize: 9, color: ev.status === 'SUBMITTED' ? '#166534' : '#94a3b8', marginTop: 2 }}>{ev.status === 'SUBMITTED' ? 'Evaluated' : 'Pending'}</div>
                    </div>
                  ))}
                  <div style={{ flex: '1 1 100px', background: 'rgb(49,154,185)', border: '1px solid rgb(39,134,165)', borderRadius: 8, padding: '8px 10px' }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: '#e0f7fa', textTransform: 'uppercase', marginBottom: 4 }}>Mean Marks</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>{pe.avg_marks !== null ? pe.avg_marks : '—'}</div>
                    <div style={{ fontSize: 9, color: '#cce7f0', marginTop: 2 }}>of {exam.max_marks}</div>
                  </div>
                </div>
                <div style={{ fontSize: 10, color: '#166534' }}>{submitted.length} of {pe.total_evaluators} evaluator(s) have submitted marks.</div>
              </div>
            );
          }
          return (
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Evaluation Status:</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#d97706', background: '#fef3c7', padding: '2px 8px', borderRadius: 12 }}>Awaiting Evaluators</span>
            </div>
          );
        })()}

        {/* University Practical — standard obtained_marks */}
        {exam.exam_type === 'UNIVERSITY' && exam.sub_type !== 'THEORY' && (
          exam.marks_status === 'SUBMITTED' ? (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#166534', textTransform: 'uppercase', marginBottom: 6 }}>Result Released</div>
              <div style={{ fontSize: 10, color: '#166534', fontWeight: 600 }}>Obtained Marks</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#14532d' }}>
                {exam.obtained_marks} <span style={{ fontSize: 12, fontWeight: 500, color: '#166534' }}>/ {exam.max_marks}</span>
              </div>
            </div>
          ) : (
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Marks Release Status:</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#d97706', background: '#fef3c7', padding: '2px 8px', borderRadius: 12 }}>Pending Release</span>
            </div>
          )
        )}

        {/* Internal Exam — theory + practical + combined */}
        {exam.exam_type === 'INTERNAL' && (
          exam.marks_status === 'SUBMITTED' ? (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#166534', textTransform: 'uppercase', marginBottom: 6 }}>Result Released</div>
              <div style={{ display: 'flex', gap: 20 }}>
                <div>
                  <div style={{ fontSize: 10, color: '#166534', fontWeight: 600 }}>Theory Marks</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#14532d' }}>{exam.theory_marks !== null && exam.theory_marks !== undefined ? exam.theory_marks : '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: '#166534', fontWeight: 600 }}>Practical/Viva Marks</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#14532d' }}>{exam.practical_marks !== null && exam.practical_marks !== undefined ? exam.practical_marks : '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: '#166534', fontWeight: 600 }}>Combined Total</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: '#14532d' }}>{exam.combined_marks !== null && exam.combined_marks !== undefined ? exam.combined_marks : '—'}</div>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Marks Release Status:</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#d97706', background: '#fef3c7', padding: '2px 8px', borderRadius: 12 }}>Pending Release</span>
            </div>
          )
        )}

        {exam.is_cancelled && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '6px 12px', fontSize: 12, color: '#dc2626', fontWeight: 700 }}>
            ❌ This examination has been cancelled.
          </div>
        )}
      </div>
    );
  };

  if (exams.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 24px', color: '#94a3b8', background: '#f8fafc', borderRadius: 12, border: '2px dashed #e2e8f0' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🗓</div>
        <div style={{ fontWeight: 700, fontSize: 15 }}>No examinations assigned yet.</div>
        <div style={{ fontSize: 13, marginTop: 4 }}>Check back when the COE publishes the timetable.</div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Summary chips */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {[
          ['Total Assigned', exams.length,      '#334155', '#f1f5f9'],
          ['Upcoming', upcoming.length, 'rgb(49,154,185)', '#e0f7fa'],
          ['Completed', completed.length,'#166534', '#dcfce7'],
        ].map(([l, v, c, bg]) => (
          <div key={l} style={{ flex: '1 1 100px', background: bg, borderRadius: 12, padding: '12px 16px' }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: c }}>{v}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: c, marginTop: 2 }}>{l}</div>
          </div>
        ))}
      </div>

      {/* Date Filter Panel */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>Filter by Date:</span>
          <input
            type="date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid #cbd5e1', fontSize: 13, outline: 'none', color: '#334155' }}
          />
          {selectedDate && (
            <button
              onClick={() => setSelectedDate('')}
              style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#f1f5f9', color: '#475569', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
            >
              Clear
            </button>
          )}
        </div>
        {selectedDate && (
          <div style={{ fontSize: 12, fontWeight: 700, color: 'rgb(49,154,185)' }}>
            Showing {activeExams.length + cancelled.length} matching exam(s)
          </div>
        )}
      </div>

      {/* Grouped Exams */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {sortedDates.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, background: '#f8fafc', borderRadius: 12, color: '#64748b', border: '1px solid #e2e8f0' }}>
            No exams scheduled on this date.
          </div>
        ) : (
          sortedDates.map(dateStr => {
            const dateExams = groupedExams[dateStr];
            return (
              <div key={dateStr} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', paddingBottom: 6, borderBottom: '2.5px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 16 }}>📅</span>
                  <span>{fmtDate(dateStr)}</span>
                  <span style={{ fontSize: 11, background: 'rgb(49,154,185)', color: '#fff', padding: '2px 8px', borderRadius: 12, marginLeft: 'auto' }}>
                    {dateExams.length} Exam{dateExams.length > 1 ? 's' : ''}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {dateExams.map(e => <ExamCard key={e.id} exam={e} />)}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Cancelled */}
      {cancelled.length > 0 && (
        <details style={{ cursor: 'pointer', border: '1px solid #fee2e2', background: '#fffafb', borderRadius: 12, padding: 12 }}>
          <summary style={{ fontSize: 13, fontWeight: 700, color: '#ef4444', outline: 'none' }}>
            Cancelled Examinations ({cancelled.length})
          </summary>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
            {cancelled.map(e => <ExamCard key={e.id} exam={e} />)}
          </div>
        </details>
      )}
    </div>
  );
}


function StudentThesisSubmissionTab() {
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [zoom, setZoom] = useState(1);

  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const backendBase = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/api\/?$/, '');
    return `${backendBase}${url}`;
  };

  const loadThesis = async () => {
    try {
      const data = await studentThesisAPI.getMyThesis();
      setSubmission(data || null);
      if (data) {
        setTitle(data.title || '');
        setDescription(data.description || '');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadThesis();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      notify.success('Thesis Title is required.');
      return;
    }
    setSaving(true);
    try {
      const data = await studentThesisAPI.createThesis({
        title: title.trim(),
        description: description.trim(),
      });
      setSubmission(data);
      setTitle(data.title);
      setDescription(data.description);
      setIsEditing(false);
    } catch (err) {
      notify.error(err?.response?.data?.error || 'Failed to submit thesis details.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      notify.success('Thesis Title is required.');
      return;
    }
    setSaving(true);
    try {
      const data = await studentThesisAPI.patchThesis({
        title: title.trim(),
        description: description.trim(),
      });
      setSubmission(data);
      setTitle(data.title);
      setDescription(data.description);
      setIsEditing(false);
    } catch (err) {
      notify.error(err?.response?.data?.error || 'Failed to update thesis details.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const data = await studentThesisAPI.uploadDocument(submission.id, formData);
      setSubmission(data);
    } catch (err) {
      notify.error(err?.response?.data?.error || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!await confirmDialog('Delete this thesis document?')) return;
    try {
      const data = await studentThesisAPI.deleteDocument(submission.id, docId);
      setSubmission(data);
      if (previewDoc && previewDoc.id === docId) {
        setPreviewDoc(null);
      }
    } catch (err) {
      notify.error(err?.response?.data?.error || 'Failed to delete document.');
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 48, color: '#64748b' }}>Loading thesis details…</div>;
  }

  const ACCENT = 'rgb(49,154,185)';
  const ACCENT_L = 'rgba(49,154,185,0.12)';
  const ACCENT_B = 'rgba(49,154,185,0.25)';

  const labelStyle = {
    fontSize: '11px',
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '.05em',
    marginBottom: '6px',
    display: 'block',
  };

  const inputStyle = {
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid #cbd5e1',
    fontSize: '14px',
    outline: 'none',
    fontFamily: 'inherit',
    width: '100%',
    boxSizing: 'border-box',
    background: '#fff',
  };

  const btnStyle = {
    padding: '10px 20px',
    borderRadius: '8px',
    border: 'none',
    background: ACCENT,
    color: '#fff',
    fontWeight: 700,
    fontSize: '13px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'background .15s',
  };

  const btnGhostStyle = {
    padding: '10px 20px',
    borderRadius: '8px',
    border: `1px solid ${ACCENT}`,
    background: '#fff',
    color: ACCENT,
    fontWeight: 700,
    fontSize: '13px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  };

  const getFileIcon = (url) => {
    if (!url) return '📎';
    if (url.endsWith('.pdf')) return '📕';
    if (/\.(jpg|jpeg|png|gif|webp)/i.test(url)) return '🖼';
    return '📄';
  };

  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", display: 'flex', flexDirection: 'column', gap: 20 }}>
      
      {/* Locked Status Banner */}
      {submission && submission.marks_approved && (
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 24 }}>🔒</span>
          <div>
            <div style={{ fontWeight: 700, color: '#166534', fontSize: 14 }}>Thesis Evaluated & Locked</div>
            <div style={{ fontSize: 12, color: '#166534', marginTop: 2 }}>
              Your thesis evaluation has been approved by the COE. Marks and documents are now locked.
            </div>
          </div>
        </div>
      )}

      {/* Main Container */}
      <div style={{ display: 'grid', gridTemplateColumns: submission ? '1.5fr 1fr' : '1fr', gap: 20 }}>
        
        {/* Thesis Details Form/Display */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
              {submission ? 'Thesis Submission Details' : 'New Thesis Submission'}
            </h3>
            {submission && !submission.marks_approved && !isEditing && (
              <button onClick={() => setIsEditing(true)} style={btnGhostStyle}>
                ✏️ Edit Details
              </button>
            )}
          </div>

          {!submission || isEditing ? (
            <form onSubmit={submission ? handleUpdate : handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={labelStyle}>Thesis Title *</label>
                <input style={inputStyle} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. A Study on Clinical Diagnostics and Data Analysis" required />
              </div>
              <div>
                <label style={labelStyle}>Abstract / Description</label>
                <textarea style={{ ...inputStyle, minHeight: 120, resize: 'vertical' }} value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe the abstract, goals, or notes of your thesis…" />
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 6 }}>
                {isEditing && (
                  <button type="button" onClick={() => { setIsEditing(false); setTitle(submission.title); setDescription(submission.description); }} style={{ ...btnGhostStyle, borderColor: '#cbd5e1', color: '#64748b' }}>
                    Cancel
                  </button>
                )}
                <button type="submit" disabled={saving} style={btnStyle}>
                  {saving ? 'Saving…' : submission ? '💾 Save Changes' : '🚀 Submit Details'}
                </button>
              </div>
            </form>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <span style={labelStyle}>Title</span>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', lineHeight: 1.4 }}>{submission.title}</div>
              </div>
              {submission.description && (
                <div>
                  <span style={labelStyle}>Abstract</span>
                  <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', background: '#f8fafc', padding: '12px 16px', borderRadius: 8, lineHeight: 1.6 }}>{submission.description}</div>
                </div>
              )}
              <div style={{ fontSize: 11, color: '#94a3b8' }}>
                First submitted: {new Date(submission.submitted_at).toLocaleString('en-IN')}
              </div>
            </div>
          )}
        </div>

        {/* Uploads and Marks Panel */}
        {submission && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            
            {/* Documents Section */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>📎 Thesis Documents</div>
              
              {(!submission.documents || submission.documents.length === 0) ? (
                <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: '20px 0' }}>No documents uploaded yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {submission.documents.map(doc => (
                    <div key={doc.id} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <span style={{ fontSize: 20 }}>{getFileIcon(doc.file_url)}</span>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.file_name}</div>
                          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>{new Date(doc.uploaded_at).toLocaleDateString('en-IN')}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => { setPreviewDoc(doc); setZoom(1); }} style={{ background: 'none', border: 'none', color: ACCENT, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Preview</button>
                        <a href={getFullUrl(doc.file_url)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: ACCENT, fontSize: 11, fontWeight: 700 }}>Open</a>
                        {!submission.marks_approved && (
                          <button onClick={() => handleDeleteDoc(doc.id)} style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Delete</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!submission.marks_approved && (
                <label style={{ display: 'block', cursor: 'pointer', marginTop: 10 }}>
                  <input type="file" style={{ display: 'none' }} accept=".pdf,.jpg,.jpeg,.png" onChange={handleUpload} disabled={uploading} />
                  <div style={{ ...btnGhostStyle, textAlign: 'center', borderStyle: 'dashed', background: '#f8fafc' }}>
                    {uploading ? 'Uploading Document…' : '➕ Upload PDF / Image Document'}
                  </div>
                </label>
              )}
            </div>

            {/* Evaluation Results Section */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>📊 Evaluation Results</div>
              {submission.marks != null ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 13, color: '#64748b' }}>Score:</span>
                    <span style={{ fontSize: 24, fontWeight: 800, color: submission.marks_approved ? '#16a34a' : '#ea580c' }}>
                      {submission.marks} marks
                    </span>
                  </div>
                  {submission.remarks && (
                    <div>
                      <span style={labelStyle}>COE Remarks:</span>
                      <div style={{ fontSize: 12, color: '#374151', background: '#f8fafc', padding: '10px 12px', borderRadius: 8, borderLeft: `3px solid ${ACCENT}` }}>
                        {submission.remarks}
                      </div>
                    </div>
                  )}
                  {submission.evaluated_at && (
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>
                      Evaluated on {new Date(submission.evaluated_at).toLocaleDateString('en-IN')}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: '10px 0' }}>
                  Evaluation pending by COE. Marks will be visible here once evaluated.
                </div>
              )}
            </div>

          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewDoc && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1100, display: 'flex', flexDirection: 'column' }} onClick={() => setPreviewDoc(null)}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', background: '#1e293b' }} onClick={e => e.stopPropagation()}>
            <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{previewDoc.file_name}</span>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <button onClick={() => setZoom(z => Math.max(0.5, z - 0.25))} style={{ background: '#334155', border: 'none', color: '#fff', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 16 }}>−</button>
              <span style={{ color: '#94a3b8', fontSize: 13 }}>{Math.round(zoom * 100)}%</span>
              <button onClick={() => setZoom(z => Math.min(3, z + 0.25))} style={{ background: '#334155', border: 'none', color: '#fff', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 16 }}>+</button>
              <button onClick={() => setPreviewDoc(null)} style={{ background: '#ef4444', border: 'none', color: '#fff', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>✕ Close</button>
            </div>
          </div>
          <div style={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: 20 }} onClick={e => e.stopPropagation()}>
            {/\.(jpg|jpeg|png|gif|webp)/i.test(previewDoc.file_url || '') ? (
              <img src={getFullUrl(previewDoc.file_url)} alt={previewDoc.file_name} style={{ transform: `scale(${zoom})`, transformOrigin: 'top center', maxWidth: '100%' }} />
            ) : (
              <iframe src={`${getFullUrl(previewDoc.file_url)}?t=${new Date().getTime()}`} title={previewDoc.file_name} style={{ width: `${100 * zoom}%`, height: `${90 * zoom}vh`, border: 'none', borderRadius: 8, minWidth: 400 }} />
            )}
          </div>
        </div>
      )}

    </div>
  );
}


const StudentDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [editingActivity, setEditingActivity] = useState(null);
  const [loadedDraft, setLoadedDraft] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if student is MBBS — used to conditionally show the MBBS tabs
  const isMBBS = user?.profile?.course === 'MBBS';
  const isThesisEligible = user?.profile?.course === 'PG' || user?.profile?.course === 'SUPER_SPECIALITY';
  const [myExaminations, setMyExaminations] = useState([]);

  const [stats, setStats] = useState({
    attendance: '0%',
    cgpa: '0.0',
    pending_fees: '₹0',
    books_issued: 0
  });
  const [upcomingExams, setUpcomingExams] = useState([]);
  const [clinicalRotations, setClinicalRotations] = useState([]);
  const [recentAttendance, setRecentAttendance] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [classAssignments, setClassAssignments] = useState([]);

  const [hasHostelAllocation, setHasHostelAllocation] = useState(false);

  useEffect(() => {
    loadDashboardData();
    hostelInventoryAPI.getMyHostelProfile()
      .then(res => {
        if (res && res.allocated) {
          setHasHostelAllocation(true);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    switch (activeTab) {
      case 'overview':
        if (upcomingExams.length === 0) loadUpcomingExams();
        if (clinicalRotations.length === 0) loadClinicalRotations();
        break;
      case 'exams':
        if (upcomingExams.length === 0) loadUpcomingExams();
        break;
      case 'clinical':
        if (clinicalRotations.length === 0) loadClinicalRotations();
        break;
      case 'attendance':
        if (recentAttendance.length === 0) loadRecentAttendance();
        break;
      case 'announcements':
        if (announcements.length === 0) loadAnnouncements();
        break;
      case 'classes':
        if (classAssignments.length === 0) loadClassAssignments();
        break;
      case 'my-examinations':
        if (myExaminations.length === 0) {
          coeAPI.getMyExaminations()
            .then(d => setMyExaminations(Array.isArray(d) ? d : []))
            .catch(() => setMyExaminations([]));
        }
        break;
      default:
        break;
    }
  }, [activeTab]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsData, examsData, rotationsData] = await Promise.all([
        studentAPI.getDashboardStats(),
        studentAPI.getUpcomingExams(),
        studentAPI.getClinicalRotations()
      ]);

      setStats(statsData);

      const examsArray = examsData?.results || examsData || [];
      const rotationsArray = rotationsData?.results || rotationsData || [];

      setUpcomingExams(Array.isArray(examsArray) ? examsArray.slice(0, 3) : []);
      setClinicalRotations(Array.isArray(rotationsArray) ? rotationsArray.slice(0, 2) : []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setStats({ attendance: '0%', cgpa: '0.0', pending_fees: '₹0', books_issued: 0 });
      setUpcomingExams([]);
      setClinicalRotations([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUpcomingExams = async () => {
    try {
      const data = await studentAPI.getUpcomingExams();
      const examsArray = data?.results || data || [];
      setUpcomingExams(Array.isArray(examsArray) ? examsArray : []);
    } catch (error) {
      setUpcomingExams([]);
    }
  };

  const loadClinicalRotations = async () => {
    try {
      const data = await studentAPI.getClinicalRotations();
      const rotationsArray = data?.results || data || [];
      setClinicalRotations(Array.isArray(rotationsArray) ? rotationsArray : []);
    } catch (error) {
      setClinicalRotations([]);
    }
  };

  const loadRecentAttendance = async () => {
    try {
      const data = await studentAPI.getRecentAttendance();
      const attendanceArray = data?.results || data || [];
      setRecentAttendance(Array.isArray(attendanceArray) ? attendanceArray : []);
    } catch (error) {
      setRecentAttendance([]);
    }
  };

  const loadAnnouncements = async () => {
    try {
      const data = await studentAPI.getAnnouncements();
      const announcementsArray = data?.results || data || [];
      setAnnouncements(Array.isArray(announcementsArray) ? announcementsArray : []);
    } catch (error) {
      setAnnouncements([]);
    }
  };

  const loadClassAssignments = async () => {
    try {
      const data = await studentAPI.getClassAssignments();
      const assignmentsArray = data?.results || data || [];
      setClassAssignments(Array.isArray(assignmentsArray) ? assignmentsArray : []);
    } catch (error) {
      setClassAssignments([]);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDaysOfWeek = (daysArray) => {
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    if (!Array.isArray(daysArray) || daysArray.length === 0) return 'N/A';
    return daysArray.sort().map(day => dayNames[day]).join(', ');
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'ACTIVE': 'badge-green', 'UPCOMING': 'badge-yellow', 'COMPLETED': 'badge-gray',
      'PRESENT': 'badge-green', 'ABSENT': 'badge-red', 'LATE': 'badge-yellow',
      'EXCUSED': 'badge-blue', 'PENDING': 'badge-orange'
    };
    return statusMap[status] || 'badge-gray';
  };

  const calculateDuration = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return `${diff} days`;
  };

  const displayName = user?.first_name
    ? `${user.first_name} ${user.last_name || ''}`.trim()
    : (user?.profile?.student_name || user?.username || 'Student');

  if (loading) {
    return (
      <DashboardLayout title={`Welcome back, ${displayName}!`}>
        <div className="loading-container">
          <div className="spinner-large"></div>
          <p>Loading dashboard...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={`Welcome back, ${displayName}!`}>
      <div className="dashboard-container">
        {/* Stats Grid */}
        <div className="stats-grid">
          <StatCard
            icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>}
            title="Attendance" value={stats.attendance} color="blue"
          />
          <StatCard
            icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>}
            title="CGPA" value={stats.cgpa} color="green"
          />
          <StatCard
            icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>}
            title="Pending Fees" value={stats.pending_fees} color="purple"
          />
          <StatCard
            icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>}
            title="Books Issued" value={stats.books_issued} color="orange"
          />
        </div>

        {/* Tabs */}
        <div className="tabs-container">
          <div className="tabs">
            <button className={`tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
              Overview
            </button>

            {/* MBBS-only tabs — hidden for PG / Super Speciality students */}
            <button
              className={`tab ${activeTab === 'my-examinations' ? 'active' : ''}`}
              onClick={() => setActiveTab('my-examinations')}
            >
              🗓 My Examinations
            </button>

            {(isMBBS || user?.profile?.course === 'PG') && (
              <>
                <button className={`tab ${activeTab === 'mbbs-schedule' ? 'active' : ''}`} onClick={() => setActiveTab('mbbs-schedule')}>
                  📚 My Classes
                </button>
                <button className={`tab ${activeTab === 'my-attendance' ? 'active' : ''}`} onClick={() => setActiveTab('my-attendance')}>
                  📊 Attendance Tracker
                </button>
              </>
            )}
            {isThesisEligible && (
              <button className={`tab ${activeTab === 'thesis' ? 'active' : ''}`} onClick={() => setActiveTab('thesis')}>
                📚 Thesis Submission
              </button>
            )}
            <button className={`tab ${activeTab === 'ethics' ? 'active' : ''}`} onClick={() => setActiveTab('ethics')}>
              ⚖️ Ethics Clearance
            </button>
            <button className={`tab ${activeTab === 'new-activity' ? 'active' : ''}`} onClick={() => { setEditingActivity(null); setLoadedDraft(null); setActiveTab('new-activity'); }}>
              ➕ New Activity
            </button>
            <button className={`tab ${activeTab === 'elogbook' ? 'active' : ''}`} onClick={() => setActiveTab('elogbook')}>
              📋 My E-Logbook
            </button>
            <button className={`tab ${activeTab === 'library' ? 'active' : ''}`} onClick={() => setActiveTab('library')}>
              📚 My Library
            </button>
            <button className={`tab ${activeTab === 'transport' ? 'active' : ''}`} onClick={() => setActiveTab('transport')}>
              🚌 Bus & Transport
            </button>
            {hasHostelAllocation && (
              <button className={`tab ${activeTab === 'hostel' ? 'active' : ''}`} onClick={() => setActiveTab('hostel')}>
                🏨 Hostel
              </button>
            )}
            <button className={`tab ${activeTab === 'calendar' ? 'active' : ''}`} onClick={() => setActiveTab('calendar')}>
              📅 Calendar
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'overview' && (
            <div className="grid-2-col">
              <div className="card">
                <div className="card-header">
                  <h2>Upcoming Exams</h2>
                  <button className="btn-link" onClick={() => setActiveTab('my-examinations')}>View All</button>
                </div>
                <div className="card-body">
                  {upcomingExams.length === 0 ? (
                    <p className="empty-state">No upcoming exams scheduled</p>
                  ) : (
                    upcomingExams.map((exam) => (
                      <div key={exam.id} className="list-item">
                        <div className="list-item-icon exam-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        </div>
                        <div className="list-item-content">
                          <h4>{exam.subject}</h4>
                          <p>{formatDate(exam.date)} at {formatTime(exam.start_time)}</p>
                        </div>
                        <span className={`badge badge-${exam.exam_type === 'THEORY' ? 'blue' : 'green'}`}>{exam.exam_type}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <h2>Clinical Rotations</h2>
                  <button className="btn-link" onClick={() => setActiveTab('my-examinations')}>View All</button>
                </div>
                <div className="card-body">
                  {clinicalRotations.length === 0 ? (
                    <p className="empty-state">No clinical rotations assigned</p>
                  ) : (
                    clinicalRotations.map((rotation) => (
                      <div key={rotation.id} className="list-item">
                        <div className="list-item-icon clinical-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                        </div>
                        <div className="list-item-content">
                          <h4>{rotation.department_name}</h4>
                          <p>{formatDate(rotation.start_date)} to {formatDate(rotation.end_date)}</p>
                        </div>
                        <span className={`badge ${getStatusBadgeClass(rotation.status)}`}>{rotation.status}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'my-examinations' && (
            <StudentMyExaminationsTab exams={myExaminations} />
          )}

          {(isMBBS || user?.profile?.course === 'PG') && activeTab === 'mbbs-schedule' && (
            <StudentClassScheduleTab />
          )}
          {(isMBBS || user?.profile?.course === 'PG') && activeTab === 'my-attendance' && (
            <StudentAttendanceTracker />
          )}
          {isThesisEligible && activeTab === 'thesis' && (
            <StudentThesisProposalSection />
          )}
          {activeTab === 'ethics' && (
            <StudentEthicsSection />
          )}
          {activeTab === 'library' && (
            <MyBorrowedBooks />
          )}
          {activeTab === 'new-activity' && (
            <NewActivityForm
              userId={user?.id}
              editingActivity={editingActivity}
              loadedDraft={loadedDraft}
              onCancel={() => { setEditingActivity(null); setLoadedDraft(null); setActiveTab('elogbook'); }}
              onSuccess={() => { setEditingActivity(null); setLoadedDraft(null); setActiveTab('elogbook'); }}
            />
          )}

          {activeTab === 'elogbook' && (
            <ActivitiesSection
              studentId={user?.id}
              userProfile={{
                name: user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : (user?.profile?.student_name || user?.username || 'N/A'),
                registration_number: user?.profile?.student_id || user?.username || 'N/A',
                email: user?.email || 'N/A',
                phone_number: user?.profile?.phone_number || user?.phone || '',
                course: user?.profile?.course || user?.course || '',
                department: user?.profile?.student_department_name || user?.profile?.student_department?.name || user?.profile?.department || user?.department || '',
                is_intern: user?.profile?.is_intern || user?.is_intern || false,
              }}
              onEditActivity={(activity) => { setEditingActivity(activity); setActiveTab('new-activity'); }}
              onNewActivity={() => { setEditingActivity(null); setLoadedDraft(null); setActiveTab('new-activity'); }}
            />
          )}

          {activeTab === 'transport' && (
            <StudentTransportViewTab />
          )}

          {activeTab === 'hostel' && (
            <HostelUserPortalTab />
          )}

          {activeTab === 'calendar' && (
            <div className="card" style={{ padding: 0, overflow: 'visible' }}>
              <Calendar />
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default StudentDashboard;